<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Plus } from "@lucide/vue";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormAlert,
  Skeleton,
} from "@/shared/ui";

import { useServicesCatalogStore } from "../model/store";
import CatalogEmptyState from "./CatalogEmptyState.vue";
import CategoryDraftRow from "./CategoryDraftRow.vue";
import CategorySection from "./CategorySection.vue";
import ServiceDetailSheet from "./ServiceDetailSheet.vue";

/**
 * Композит каталогу послуг (spec §4.2/§9): один `Card` зі списком груп +
 * draft-рядок унизу. Інкапсулює стани list (loading/error/ready/empty).
 * T4: монтує ServiceDetailSheet — відкривається тапом на ServiceRow.
 */
const { t } = useI18n();
const store = useServicesCatalogStore();

const openServiceId = ref<string | null>(null);

onMounted(() => {
  void store.load();
});
</script>

<template>
  <Card data-testid="services-catalog">
    <CardHeader>
      <div
        class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
        data-testid="catalog-header"
      >
        <div>
          <CardTitle>{{ t("services.title") }}</CardTitle>
          <CardDescription>{{ t("services.subtitle") }}</CardDescription>
        </div>
        <Button
          v-if="!store.isFirstRun"
          variant="default"
          size="sm"
          class="w-full sm:w-auto max-sm:h-11"
          data-testid="toolbar-new-category"
          @click="store.openCategoryDraft()"
        >
          <Plus :size="16" aria-hidden="true" />
          {{ t("services.categories.add") }}
        </Button>
      </div>
    </CardHeader>

    <CardContent>
      <!-- visually-hidden polite статус-регіон: озвучує success без toast-спаму -->
      <p
        class="sr-only"
        role="status"
        aria-live="polite"
      >
        {{ store.politeMessage }}
      </p>

      <!-- loading: skeleton-рядки -->
      <div
        v-if="store.listStatus === 'loading'"
        data-testid="services-loading"
        aria-busy="true"
        class="flex flex-col gap-2"
      >
        <span class="sr-only">{{ t("services.loading") }}</span>
        <Skeleton
          v-for="n in 3"
          :key="n"
          class="h-9 w-full"
        />
      </div>

      <!-- error: локальний inline-error + retry -->
      <div
        v-else-if="store.listStatus === 'error'"
        data-testid="services-error"
        class="flex flex-col gap-3"
      >
        <FormAlert
          variant="destructive"
          :message="t('services.listError')"
        />
        <div>
          <Button
            variant="outline"
            size="sm"
            @click="store.load()"
          >
            {{ t("services.retry") }}
          </Button>
        </div>
      </div>

      <!-- ready: draft on top / empty-state / grouped list -->
      <div
        v-else
        class="flex flex-col"
      >
        <!-- 1. Top category draft (on-demand) -->
        <CategoryDraftRow v-if="store.categoryDraftOpen" />

        <!-- 2. First-run empty state -->
        <CatalogEmptyState v-else-if="store.isFirstRun" />

        <!-- 3. Normal grouped list -->
        <template v-else>
          <CategorySection
            v-for="group in store.groups"
            :key="group.category?.id ?? '__uncategorized__'"
            :group="group"
            @open="(id) => { openServiceId = id; }"
          />
        </template>
      </div>
    </CardContent>
  </Card>

  <!-- Detail sheet (T4/T5): lazy — only renders when a service is targeted -->
  <ServiceDetailSheet
    v-if="openServiceId !== null"
    :service-id="openServiceId"
    :open="openServiceId !== null"
    @update:open="(v) => { if (!v) openServiceId = null; }"
  />
</template>
